import { Form, Input } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPasswordChecklist from "react-password-checklist";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../apis/store";
import { toSignup } from "../../apis/actions/auth.actions";
import { showNotitfication } from "../../utils/Utils";
import { Link } from "react-router-dom";
import SaudiSvg from "../../assets/saudi-svg.svg";
import {
  AuthIconArrowForward,
  AuthIconLock,
  AuthIconUser,
} from "../../Components/icons/AuthFormIcons";
import RegisterOtpModal from "./RegisterOtp";
import PhoneLogo from "../../styles/images/phone-svg-black.svg";

interface Props {
  setToRegister: () => void;
  /** When true, render only the card + OTP modal (used inside Login’s animated panel). */
  embedded?: boolean;
}

const Register = ({ setToRegister, embedded = false }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");
  const [form] = Form.useForm();
  const [password, setPassword] = useState<string>("");
  const [passwordAgain, setPasswordAgain] = useState<string>("");
  const [checked, setChecked] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);

  const handleCloseOtpModal = () => {
    setIsModalVisible(false);
  };
  const validateSaudiPhone = (_: any, value: any) => {
    const saudiPhoneRegex = /^5[0-9]{8}$/;
    if (!value) {
      return Promise.reject(new Error(t("otp.warning.phone")));
    }
    if (!saudiPhoneRegex.test(value)) {
      return Promise.reject(new Error(t("register.invalidSaudiPhone")));
    }
    return Promise.resolve();
  };

  const onFinish = async () => {
    try {
      await form.validateFields();
      const success = (await dispatch(
        toSignup({
          data: {
            mobile: form.getFieldValue("phone"),
            code: "966",
          },
          lang: i18n.language,
        })
      )) as any;
      const login = success?.payload?.data?.result;

      if (login?.code === 200) {
        setIsModalVisible(true);
        const seconds = login?.time_left || 0;
        setTimeLeft(seconds);
      } else if (login?.code == 409) {
        const message =
          i18n.language === "ar"
            ? "تم التسجيل مسبقاً برقم الجوال المدخل. يرجى استخدام رقم جوال آخر"
            : "The entered mobile number is already registered. Please use another mobile number";
        showNotitfication(message, "error");
      } else {
        showNotitfication(login?.message, "error");
      }
    } catch (error) {}
  };

  const card = (
    <div className="ac-login-card">
      <div className="login-content">
        <div className="text-center ac-login-card__head">
          {!embedded && (
            <span className="ac-login-card__badge ac--font-expo text-sm-bold">
              {t("login.register")}
            </span>
          )}
          <h4 className="neutral-1000 ac--font-expo ac-login-card__title">
            {t("register.createAccount")}
          </h4>
        </div>
              <div className="form-login mt-30">
                <p className="text-muted mb-3 ac--font-expo">
                  {t("register.allFieldsRequired")}
                </p>
                <Form form={form}>
                  <Form.Item
                    name="username"
                    rules={[
                      {
                        required: true,
                        message: t("profile.messages.5"),
                      },
                    ]}
                  >
                    <div className="form-group ac--font-expo">
                      <Input
                        className="ac--input-login"
                        size="large"
                        type="text"
                        placeholder={t("register.fullname")}
                        prefix={<AuthIconUser />}
                      />
                    </div>
                  </Form.Item>
                  <Form.Item
                    label=""
                    name="phone"
                    rules={[
                      {
                        validator: validateSaudiPhone,
                      },
                    ]}
                  >
                    <div
                      className="form-group ac--font-expo d-flex align-items-stretch w-100 gap-2"
                      style={{ minWidth: 0 }}
                    >
                      {!isRtl && (
                        <span className="d-flex align-items-center flex-shrink-0 px-1">
                          <img
                            src={SaudiSvg}
                            alt=""
                            width={24}
                            height={16}
                            style={{ objectFit: "contain" }}
                          />
                          <span
                            className="fw-bold ms-1"
                            style={{ fontSize: "14px", whiteSpace: "nowrap" }}
                          >
                            +966
                          </span>
                        </span>
                      )}
                      <div className="flex-grow-1 min-w-0">
                        <Input
                          className={`ac--input-login ${
                            isRtl ? "ac--placeholder-rtl" : ""
                          }`}
                          size="large"
                          type="tel"
                          placeholder="5XXXXXXXX"
                          prefix={
                            <img
                              src={PhoneLogo}
                              alt=""
                              width={20}
                              height={20}
                              className={isRtl ? "ms-1" : "me-1"}
                            />
                          }
                          maxLength={9}
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                        />
                      </div>
                      {isRtl && (
                        <span
                          className="d-flex align-items-center flex-shrink-0 px-1"
                          dir="ltr"
                        >
                          <img
                            src={SaudiSvg}
                            alt=""
                            width={24}
                            height={16}
                            style={{ objectFit: "contain" }}
                          />
                          <span
                            className="fw-bold ms-1"
                            style={{ fontSize: "14px", whiteSpace: "nowrap" }}
                          >
                            +966
                          </span>
                        </span>
                      )}
                    </div>
                  </Form.Item>
                <span
                  className="ac--font-expo text-muted d-block mb-2"
                  style={{ fontSize: "15px", marginTop: "2px" }}
                >
                  {isRtl
                    ? "للتحقق من رقمك، سنرسل لك رسالة نصية تحتوي على رمز مؤقت."
                    : "To verify your number, we will send you SMS with an OTP."}{" "}
                </span>
                <Form.Item
                  label=""
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: t("profile.messages.4"),
                    },
                  ]}
                >
                  <div className="form-group ac--password-container">
                    <Input.Password
                      className="ac--input-login"
                      size="large"
                      type="password"
                      placeholder="***********************"
                      prefix={<AuthIconLock />}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                </Form.Item>
                <Form.Item
                  label=""
                  name="password2"
                  rules={[
                    {
                      required: true,
                      message: t("profile.messages.4"),
                    },
                  ]}
                >
                  <div className="form-group">
                    <Input.Password
                      className="ac--input-login"
                      size="large"
                      type="password"
                      placeholder="***********************"
                      onChange={(e) => setPasswordAgain(e.target.value)}
                      prefix={<AuthIconLock />}
                    />
                  </div>
                </Form.Item>
                <ReactPasswordChecklist
                  rules={["minLength", "number", "capital", "match"]}
                  minLength={6}
                  value={password}
                  className="ac--font-expo"
                  valueAgain={passwordAgain}
                  onChange={(isValid) => {
                    setIsValid(isValid);
                  }}
                  messages={{
                    minLength: t("authentication.message.message1"),
                    number: t("authentication.message.message2"),
                    capital: t("authentication.message.message3"),
                    match: t("authentication.message.message4"),
                  }}
                />

                <div className="form-group my-3">
                  <div className="box-remember-forgot">
                    <div className="remeber-me d-flex align-items-center neutral-500">
                      <input
                        className={`cb-remember ${
                          isRtl ? "ml-5" : "mr-5"
                        }`}
                        type="checkbox"
                        onClick={() => setChecked(!checked)}
                      />
                      <span className={isRtl ? "ac--font-expo" : ""}>
                        {t("register.agree")}

                        <Link
                          to="/terms-conditions"
                          className={`ac--link-terms ${
                            isRtl ? "mr-5" : "ml-5"
                          }`}
                        >
                          {t("register.terms")}
                        </Link>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="form-group mb-30">
                  <button
                    type="button"
                    className="btn btn-primary w-100 ac--font-expo ac-login-page__submit"
                    disabled={!checked || !isValid}
                    onClick={onFinish}
                  >
                    {t("register.signUp")}
                    <span
                      className={`ac-auth-arrow-slot ${
                        isRtl ? "ac-auth-arrow--rtl" : ""
                      }`}
                    >
                      <AuthIconArrowForward size={17} />
                    </span>
                  </button>
                </div>

                <p className="text-sm-medium neutral-500 text-center mt-30 ac--font-expo">
                  {t("login.haveAccount")}
                  <button
                    type="button"
                    className="ac-login-page__register-link border-0 bg-transparent p-0 ms-1"
                    onClick={() => setToRegister()}
                  >
                    {t("register.login")}
                  </button>
                </p>
              </Form>
            </div>
          </div>
        </div>
  );

  const otpModal = (
    <RegisterOtpModal
      open={isModalVisible}
      onClose={handleCloseOtpModal}
      phone={form.getFieldValue("phone")}
      OtpTimeLeft={timeLeft}
      name={form.getFieldValue("username")}
      password={form.getFieldValue("password")}
    />
  );

  if (embedded) {
    return (
      <>
        {card}
        {otpModal}
      </>
    );
  }

  return (
    <div className="ac-login-register-wrap">
      <div className="row justify-content-center ac-login-page__grid">
        <div className="col-xxl-7 col-xl-8 col-lg-10 ac-login-page__form-col">
          {card}
        </div>
      </div>
      {otpModal}
    </div>
  );
};

export default Register;
